import { Trans } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { textPBold } from '@features/ui/styles/textStyles';

export enum SENTENCE_TENSE {
  FUTURE = 'Future',
  CURRENT = 'Current',
  PAST = 'Past',
}

export enum SENTENCE_PLURALISATION {
  PLURAL= 'Plural',
  SINGULAR = 'Singular'
}

export const pastOrFuture = (date): SENTENCE_TENSE => (DateTime.now().diff(DateTime.fromISO(date), 'days').toObject().days <= 0 ? SENTENCE_TENSE.FUTURE : SENTENCE_TENSE.PAST);

export const isToday = (date: DateTime) => isUnderWay(date.startOf('day'), date.endOf('day'));

export const isUnderWay = (startDate: DateTime, endDate: DateTime): Boolean => {
  const now = DateTime.now();
  return now >= startDate && now <= endDate;
};

export const isMultiplesDays = (startDate: DateTime, endDate ?: DateTime) => {
  if (!endDate || !endDate.isValid) {
    return false;
  }
  const { days } = endDate.diff(startDate, ['days']);
  return days >= 1;
};

export const singleOrMultipleDay = (dateFrom, dateTo): SENTENCE_PLURALISATION => (dateFrom
  && dateTo ? SENTENCE_PLURALISATION.PLURAL : SENTENCE_PLURALISATION.SINGULAR);

export const bodySentence = (dateFrom, dateTo, currentLang, traductionPrefix, extraVars = {}) => {
  const startDate = DateTime.fromISO(dateFrom).setLocale(currentLang).toFormat('cccc dd.MM.yyyy');
  const endDate = DateTime.fromISO(dateTo).setLocale(currentLang).toFormat('cccc dd.MM.yyyy');
  return ({
    [SENTENCE_TENSE.FUTURE]: {
      [SENTENCE_PLURALISATION.SINGULAR]: (
        <Trans
          i18nKey={`${traductionPrefix}futureSingularSentence.message`}
          values={{
            startDate,
            ...extraVars,
          }}
        >
          The event will be the
          {' '}
          <Bold>StartDate</Bold>
        </Trans>
      ),
      [SENTENCE_PLURALISATION.PLURAL]: (
        <Trans
          i18nKey={`${traductionPrefix}futurePluralSentence.message`}
          values={{
            startDate,
            endDate,
            ...extraVars,
          }}
        >
          The event will take between
          {' '}
          <Bold>StartDate</Bold>
          {' '}
          and the
          {' '}
          <Bold>EndDate</Bold>
        </Trans>
      ),
    },
    [SENTENCE_TENSE.CURRENT]: {
      [SENTENCE_PLURALISATION.SINGULAR]: (
        <Trans
          i18nKey={`${traductionPrefix}currentSingularSentence.message`}
          values={{
            startDate,
            ...extraVars,
          }}
        >
          The event will be the
          {' '}
          <Bold>StartDate</Bold>
        </Trans>
      ),
      [SENTENCE_PLURALISATION.PLURAL]: (
        <Trans
          i18nKey={`${traductionPrefix}currentPluralSentence.message`}
          values={{
            startDate,
            endDate,
            ...extraVars,
          }}
        >
          The event will take between
          {' '}
          <Bold>StartDate</Bold>
          {' '}
          and the
          {' '}
          <Bold>EndDate</Bold>
        </Trans>
      ),
    },
    [SENTENCE_TENSE.PAST]: {
      [SENTENCE_PLURALISATION.SINGULAR]: (
        <Trans
          i18nKey={`${traductionPrefix}pastSingularSentence.message`}
          values={{
            startDate,
            endDate,
            ...extraVars,
          }}
        >
          The event was the
          {' '}
          <Bold>StartDate</Bold>
        </Trans>
      ),
      [SENTENCE_PLURALISATION.PLURAL]: (
        <Trans
          i18nKey={`${traductionPrefix}pastPluralSentence.message`}
          values={{
            startDate,
            endDate,
            ...extraVars,
          }}
        >
          The event was between
          {' '}
          <Bold>StartDate</Bold>
          {' '}
          and the
          {' '}
          <Bold>EndDate</Bold>
        </Trans>
      ),
    },
  });
};

export const relativeTime = (dateFrom, sentenceTense: SENTENCE_TENSE, t, traductionPrefix) => {
  const start = DateTime.now().startOf('day');
  const end = DateTime.fromISO(dateFrom);
  const diff = end.diff(start, ['years', 'months', 'days']);
  const { values } = diff;

  const roundDate = sentenceTense === SENTENCE_TENSE.PAST ? Math.floor : Math.ceil;

  if (values.years && Math.abs(values.years) > 0) {
    // console.log('[DEBUG] Relative time translation key:', `${traductionPrefix}relativeTimeMoreThanOneYear${sentenceTense}`);
    return t(`${traductionPrefix}relativeTimeMoreThanOneYear${sentenceTense}.message`,
      { years: Math.floor(Math.abs(values.years)) });
  } if (values.months && Math.abs(values.months) > 0) {
    // console.log('[DEBUG] Relative time translation key:', `${traductionPrefix}relativeTimeMonths${sentenceTense}`);
    return t(`${traductionPrefix}relativeTimeMonths${sentenceTense}.message`,
      { months: Math.floor(Math.abs(values.months)) });
  }
  // console.log('[DEBUG] Relative time translation key:', `${traductionPrefix}relativeTimeDays${sentenceTense}`);
  return t(`${traductionPrefix}relativeTimeDays${sentenceTense}.message`, { days: roundDate(Math.abs(values.days)) });
};

const Bold = styled.b`
  ${textPBold}
`;
